import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { restartableTask, dropTask } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import CustomerModel from 'cio/models/customer';
import ApiService from 'cio/services/api';
import { MergedAttributes, PreviewResponse, ProfileType, Step } from 'cio/utils/people-merge';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import config from 'cio/config/environment';
import RouteHistoryService from 'cio/services/route-history';
import RouterService from '@ember/routing/router-service';

const ATTRIBUTES_SEARCHED_EVENT_DEBOUNCE_DURATION = 2000;

export default class PeopleMergeController extends Controller {
  @service account!: AccountService;
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service routeHistory!: RouteHistoryService;
  @service router!: RouterService;

  declare model: {
    primary: CustomerModel | null;
    secondary: CustomerModel | null;
  };

  queryParams = [{ primaryCustomerId: { as: 'primary_id' }, secondaryCustomerId: { as: 'secondary_id' } }];

  readonly Step = Step;

  @tracked selectedCustomers = this.model;
  @tracked primaryCustomerId?: string;
  @tracked secondaryCustomerId?: string;
  @tracked searchTerm = '';
  @tracked mergedAttributes: MergedAttributes = [];
  @tracked mergeSummary?: PreviewResponse['summary'];

  @tracked activeStepId = Step.Setup;

  @tracked mergeConfirmation1 = false;
  @tracked mergeConfirmation2 = false;

  @tracked isShowingMergeFeedbackModal = false;

  // peopleCards is used to render the customer selection card components - this
  // array is looped through, and id is used as the loop key, which makes Ember
  // reuse the components on swap etc, so that we don't lose internal component
  // state. We need to be careful though to make sure we don't have the same id
  // in both, otherwise we invite weird bugs
  @tracked peopleCards: { type: ProfileType; id: string }[] = [];

  get canMerge() {
    return this.auth.hasPermission('journeys/people/edit');
  }

  setDefaults() {
    this.selectedCustomers = this.model;
    this.peopleCards = [
      { type: ProfileType.Primary, id: this.selectedCustomers.primary?.id ?? '_primary_empty' },
      { type: ProfileType.Secondary, id: this.selectedCustomers.secondary?.id ?? '_secondary_empty' },
    ];
    this.activeStepId = Step.Setup;
    this.maybeFetchPreview();
    this.mergeConfirmation1 = false;
    this.mergeConfirmation2 = false;
    this.isShowingMergeFeedbackModal = false;
  }

  reset() {
    this.selectedCustomers = { primary: null, secondary: null };
    this.peopleCards = [
      { type: ProfileType.Primary, id: '_primary_empty' },
      { type: ProfileType.Secondary, id: '_secondary_empty' },
    ];
    this.mergedAttributes = [];
    this.mergeSummary = undefined;
    this.searchTerm = '';
    this.updateQueryParams();
    this.mergeConfirmation1 = false;
    this.mergeConfirmation2 = false;
    this.isShowingMergeFeedbackModal = false;
  }

  cancelButtonParams: { route: string; models: string[]; query?: Record<string, any> } = {
    route: 'people',
    models: [],
    query: {},
  };

  @action
  setCancelButtonParams() {
    let { previousRoute } = this.routeHistory;

    let buttonParams = {
      route: this.selectedCustomers.primary != null ? 'person' : 'people',
      models: this.selectedCustomers.primary != null ? [this.selectedCustomers.primary.id] : [],
      query: {},
    };

    // Use Analytics params to construct "Cancel" link
    if (previousRoute?.name.includes('people.list')) {
      buttonParams = {
        route: 'people',
        models: [],
        query: previousRoute.queryParams ?? {},
      };
    }

    if (previousRoute?.name.includes('person')) {
      let { parent } = previousRoute;

      while (parent != null && parent.name !== 'person') {
        parent = parent.parent;
      }

      if (parent != null) {
        // "any" because paramNames is not typed, but does exist on the object
        let otherModelParams = (previousRoute as any).paramNames.map((name: string) => previousRoute?.params[name]);
        buttonParams = {
          route: previousRoute.name,
          models: [parent.params.person_id, ...otherModelParams],
          query: {},
        };
      }
    }

    this.cancelButtonParams = buttonParams;
  }

  get noCustomersSelected() {
    return this.selectedCustomers.primary == null && this.selectedCustomers.secondary == null;
  }

  get hideAttributeSearchInput() {
    return this.noCustomersSelected && this.activeStepId !== Step.Preview;
  }

  @action
  onAttributeSearchVisibilityChange() {
    if (this.hideAttributeSearchInput) {
      this.searchTerm = '';
    }
  }

  get steps() {
    return [
      {
        id: Step.Setup,
        label: 'Setup',
        active: this.activeStepId === Step.Setup,
        disabled: false,
        complete: this.hasRequiredSetup,
      },
      {
        id: Step.Preview,
        label: 'Review and confirm',
        active: this.activeStepId === Step.Preview,
        disabled: !this.hasRequiredSetup,
        complete: false,
      },
    ];
  }

  get hasRequiredSetup() {
    return this.primaryCustomerId && this.secondaryCustomerId && this.primaryCustomerId !== this.secondaryCustomerId;
  }

  get primaryCustomer() {
    return this.selectedCustomers.primary;
  }

  get secondaryCustomer() {
    return this.selectedCustomers.primary;
  }

  onSearchTermInput = restartableTask(async () => {
    await timeout(ATTRIBUTES_SEARCHED_EVENT_DEBOUNCE_DURATION);

    this.analytics.track('people', 'merge_attributes_searched', {
      source: this.activeStepId,
      searchTerm: this.searchTerm,
    });
  });

  previewMergeTask = dropTask(async () => {
    try {
      let { attributes, summary } = (await this.api.post('customers/merge/preview', {
        data: {
          customer_ids: [this.primaryCustomerId, this.secondaryCustomerId],
          primary_customer_id: this.primaryCustomerId,
        },
      })) as PreviewResponse;

      this.mergedAttributes = attributes;
      this.mergeSummary = summary;
    } catch {}
  });

  previewMergeAndGoToNextStepTask = dropTask(async () => {
    this.changeStep(Step.Preview);
    await this.previewMergeTask.perform();
  });

  goToSetupStepTask = dropTask(async () => {
    this.changeStep(Step.Setup);
    if (this.hasRequiredSetup) {
      await this.previewMergeTask.perform();
    }

    this.mergeConfirmation1 = false;
    this.mergeConfirmation2 = false;
  });

  confirmAndMergeTask = dropTask(async () => {
    await this.api.post('customers/merge', {
      data: {
        customer_ids: [this.primaryCustomerId, this.secondaryCustomerId],
        primary_customer_id: this.primaryCustomerId,
      },
    });

    this.alerts.success(
      'people.merge.success',
      'The profiles are now merging. This can take a few seconds to complete.',
      undefined,
      config.APP.alertDurationLong,
      {
        title: 'Merge another profile',
        link: {
          route: 'people.merge',
          query: {
            source: 'merge_success_banner',
          },
        },
      },
    );

    this.router.transitionTo('person', this.primaryCustomerId as string);
  });

  @action
  changeStep(step: Step) {
    if (this.activeStepId === Step.Preview && step === Step.Setup) {
      this.analytics.track('people', 'merge_setup_tab_viewed', {
        source: 'review_and_confirm_tab',
        model: this.selectedCustomers,
      });
    } else if (this.activeStepId === Step.Setup && step === Step.Preview) {
      this.analytics.track('people', 'merge_review_and_confirm_tab_viewed', {
        customers: this.selectedCustomers,
        summary: { ...this.mergeSummary },
      });
    }

    this.activeStepId = step;
  }

  @action
  selectCustomer(profileType: ProfileType, customer: CustomerModel | null) {
    this.selectedCustomers = {
      ...this.selectedCustomers,
      [profileType]: customer,
    };

    if (profileType === ProfileType.Primary) {
      this.peopleCards = [
        { type: ProfileType.Primary, id: customer?.id ?? '_primary_empty' },
        { type: ProfileType.Secondary, id: this.peopleCards[1].id },
      ];
    } else {
      this.peopleCards = [
        { type: ProfileType.Primary, id: this.peopleCards[0].id },
        { type: ProfileType.Secondary, id: customer?.id ?? '_secondary_empty' },
      ];
    }

    if (customer == null) {
      this.mergeSummary = undefined;
    }

    this.updateQueryParams();
    this.maybeFetchPreview();
  }

  @action
  swapCustomers() {
    this.selectedCustomers = {
      primary: this.selectedCustomers.secondary,
      secondary: this.selectedCustomers.primary,
    };

    this.peopleCards = [
      { type: ProfileType.Primary, id: this.peopleCards[1].id },
      { type: ProfileType.Secondary, id: this.peopleCards[0].id },
    ];

    this.updateQueryParams();
    this.maybeFetchPreview();

    this.analytics.track('people', 'merge_people_swapped');
  }

  @action
  showMergeFeedbackModal() {
    this.isShowingMergeFeedbackModal = true;

    this.analytics.track('people', 'merge_feedback_request_started', { source: Step.Preview });
  }

  private updateQueryParams() {
    this.primaryCustomerId = this.selectedCustomers.primary?.id;
    this.secondaryCustomerId = this.selectedCustomers.secondary?.id;
  }

  private maybeFetchPreview() {
    if (this.hasRequiredSetup) {
      this.previewMergeTask.perform();
    }
  }
}

declare module '@ember/controller' {
  interface Registry {
    'people.merge': PeopleMergeController;
  }
}
